import { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CustomWarning from '../../shared/components/CustomWarning';
import Notification from '../../shared/components/Notification';
import { NotificationTypes } from '../../enums/notificationTypes';
import { useUsersList } from '../../hooks/useUsersList';
import UserManagerService, { getUserManagerErrorMessage } from '../../services/UserManager/userManager.service';
import { userManagerServiceMessages } from '../../services/UserManager/userManager.constants';
import UsersTable from './components/UsersTable';
import UserFormModal from './components/UserFormModal';
import UserProfileModal from './components/UserProfileModal';
import { DeleteStep, UserAction } from './constants';
import type { UserRecord } from '../../types/UserManagementTypes';
import styles from './UserManagement.module.scss';

const { Title } = Typography;

const DELETE_KEYWORD = 'DELETE';
const DELETE_WARNING_MESSAGE = 'This will permanently delete the user and user data.';
const DELETE_CONFIRM_MESSAGE = `Are you sure? If you wish to delete this user, type "${DELETE_KEYWORD}" in the field below.`;

interface DeleteFlow {
  user: UserRecord;
  step: DeleteStep;
  anchor: HTMLElement | null;
  // Kept false through the close animation so the dialog exits where it opened.
  open: boolean;
}

// Which flow the stepper modal is serving. Kept through the close animation so
// the title doesn't change as the modal exits.
interface UserFormFlow {
  isEditing: boolean;
  userId?: number;
  open: boolean;
}

// Row actions carry a user and the ⋮ they came from; header actions don't.
type UserActionHandler = (user?: UserRecord, anchor?: HTMLElement | null) => void;

const UserManagement = () => {
  const { users, refreshUsers, isLoadingUsers, isUsersLoadingError, isFetchingNextPage, hasNextPage, fetchNextPage, sortBy, sortOrder, setSort } =
    useUsersList();
  const [deleteFlow, setDeleteFlow] = useState<DeleteFlow | null>(null);
  const [formFlow, setFormFlow] = useState<UserFormFlow | null>(null);
  const [profileUserId, setProfileUserId] = useState<number | undefined>(undefined);
  const { deleteUser, getRolesAvailability } = UserManagerService();
  const { mutate: deleteUserMutate, isPending: isDeleting } = useMutation({ ...deleteUser() });
  const queryClient = useQueryClient();

  // Warms the cache on landing so Create/Edit's role selector is ready instantly.
  useEffect(() => {
    queryClient.prefetchQuery(getRolesAvailability());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeFormFlow = useCallback(() => setFormFlow((flow) => (flow ? { ...flow, open: false } : flow)), []);

  const closeDeleteFlow = useCallback(() => setDeleteFlow((flow) => (flow ? { ...flow, open: false } : flow)), []);

  // Only discard once the dialog has finished closing -- the step change below
  // also closes a modal, and that must not drop the flow mid-way.
  const handleAfterClose = useCallback(() => setDeleteFlow((flow) => (flow && !flow.open ? null : flow)), []);

  const handleDeleteWarningConfirm = useCallback(() => {
    setDeleteFlow((flow) => (flow ? { ...flow, step: DeleteStep.KEYWORD } : flow));
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteFlow?.user) return;
    deleteUserMutate(Number(deleteFlow.user.id), {
      onSuccess: (response) => {
        Notification({
          message: response.message || 'User deleted successfully',
          type: NotificationTypes.SUCCESS,
        });
        refreshUsers();
        closeDeleteFlow();
      },
      onError: (error) =>
        Notification({
          message: userManagerServiceMessages.USERS.DELETE_ERROR,
          description: getUserManagerErrorMessage(error),
          type: NotificationTypes.ERROR,
        }),
    });
  }, [deleteFlow, deleteUserMutate, refreshUsers, closeDeleteFlow]);

  const startDeleteFlow = useCallback((user?: UserRecord, anchor?: HTMLElement | null) => {
    if (user) setDeleteFlow({ user, step: DeleteStep.WARNING, anchor: anchor ?? null, open: true });
  }, []);

  const handleAddUser = useCallback(() => setFormFlow({ isEditing: false, open: true }), []);
  const handleEditUser = useCallback(
    (user?: UserRecord) => setFormFlow({ isEditing: true, userId: user ? Number(user.id) : undefined, open: true }),
    [],
  );
  const handleViewUserProfile = useCallback((user?: UserRecord) => {
    if (user) setProfileUserId(Number(user.id));
  }, []);

  const handleEditFromProfile = useCallback((userId: number) => {
    setProfileUserId(undefined);
    setFormFlow({ isEditing: true, userId, open: true });
  }, []);

  // Record<UserAction, …> rather than a switch: a new action fails to compile
  // until it has a handler here, so extending is an addition, never an edit.
  const userActionHandlers = useMemo<Record<UserAction, UserActionHandler>>(
    () => ({
      [UserAction.ADD_USER]: handleAddUser,
      [UserAction.EDIT_USER]: handleEditUser,
      [UserAction.VIEW_USER_PROFILE]: handleViewUserProfile,
      [UserAction.DELETE_USER]: startDeleteFlow,
    }),
    [handleAddUser, handleEditUser, handleViewUserProfile, startDeleteFlow],
  );

  // Optional call: the menu hands back a plain string, so an id outside the
  // enum no-ops here instead of throwing.
  const handleUserAction = useCallback(
    (action: UserAction, user?: UserRecord, anchor?: HTMLElement | null) => userActionHandlers[action]?.(user, anchor),
    [userActionHandlers],
  );

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <Title level={2} className={styles.title}>
          User Manager
        </Title>
        <button type="button" className={styles.addButton} onClick={handleAddUser}>
          <img src="/assets/upload-file-icon.svg" alt="" className={styles.addIcon} />
          Add New User
        </button>
      </header>
      <div className={styles.content}>
        <UsersTable
          users={users}
          isLoadingUsers={isLoadingUsers}
          isUsersLoadingError={isUsersLoadingError}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={setSort}
          onAction={handleUserAction}
        />
      </div>
      <UserFormModal
        open={!!formFlow?.open}
        onClose={closeFormFlow}
        isEditing={!!formFlow?.isEditing}
        userId={formFlow?.userId}
        onCreated={refreshUsers}
      />
      <UserProfileModal
        open={!!profileUserId}
        userId={profileUserId}
        onClose={() => setProfileUserId(undefined)}
        onEdit={handleEditFromProfile}
      />
      <CustomWarning
        open={!!deleteFlow?.open && deleteFlow.step === DeleteStep.WARNING}
        anchorEl={deleteFlow?.anchor}
        afterClose={handleAfterClose}
        onCancel={closeDeleteFlow}
        onConfirm={handleDeleteWarningConfirm}
        message={DELETE_WARNING_MESSAGE}
        cancelText="No, Go Back"
        confirmText="Yes, Delete"
        variant="dark"
      />
      <CustomWarning
        open={!!deleteFlow?.open && deleteFlow.step === DeleteStep.KEYWORD}
        anchorEl={deleteFlow?.anchor}
        afterClose={handleAfterClose}
        onCancel={closeDeleteFlow}
        onConfirm={handleDeleteConfirm}
        message={DELETE_CONFIRM_MESSAGE}
        cancelText="Cancel"
        confirmText="Confirm Delete"
        confirmKeyword={DELETE_KEYWORD}
        keywordPlaceholder=""
        variant="dark"
        size="large"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default UserManagement;
